import { useState } from 'react';
import { Pencil, StickyNote } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { useDraft } from '@/hooks/useDraft';
import { DraftHinweis } from '@/components/ui/DraftHinweis';
import { ErwaehnungTextarea } from '@/components/ui/ErwaehnungTextarea';

type Props = {
  bestellungId: number;
  value: string | null;
  /** true = tatsaechlich gespeichert (false z. B. bei abgelehnter Rueckfrage). */
  onSave: (notiz: string | null) => Promise<boolean>;
};

export function NotizSektion({ bestellungId, value, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [entwurf, setEntwurf] = useState(value ?? '');
  const [laeuft, setLaeuft] = useState(false);
  const auth = useAuth();

  // Entwurf nur waehrend des Bearbeitens sichern, und nur wenn er vom
  // gespeicherten Stand abweicht (S-1).
  const draft = useDraft<string>({
    scope: `notiz-${bestellungId}`,
    userId: auth.data?.user.id ?? null,
    wert: entwurf,
    aktiv: editing,
    istLeer: (w) => w.trim() === '' || w === (value ?? '')
  });

  async function speichern(): Promise<void> {
    setLaeuft(true);
    try {
      const ok = await onSave(entwurf.trim() === '' ? null : entwurf);
      if (!ok) return; // Rueckfrage abgelehnt: Editor offen lassen
      draft.loeschen();
      setEditing(false);
    } catch {
      // Fehlgeschlagen: Text und Entwurf stehen lassen, damit nichts
      // verlorengeht. Die Meldung kommt aus der Mutation.
    } finally {
      setLaeuft(false);
    }
  }

  // SEIT 02.11.2026 GANZ OBEN (Betreiber: „erste Priorität"). Mit Inhalt
  // ist sie eine Karte mit Goldrand und Lesegröße, damit sie beim Öffnen
  // der Bestellung als Erstes gelesen wird. Ohne Inhalt bleibt nur ein
  // schmaler Knopf — eine leere Karte oben wäre Platz für nichts.
  return (
    <section aria-label="Notiz zur Bestellung" data-testid="notiz-sektion">
      {editing ? (
        <div className="space-y-2 rounded-md border-2 border-gold bg-cream-warm p-3">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gold-dark">
            <StickyNote aria-hidden className="h-4 w-4" />
            Notiz zur Bestellung
          </p>
          {draft.angebot !== null && (
            <DraftHinweis
              text="eine ungespeicherte Notiz"
              onWiederherstellen={() => {
                const d = draft.annehmen();
                if (d !== null) setEntwurf(d);
              }}
              onVerwerfen={draft.verwerfen}
            />
          )}
          {/* @ öffnet die Mitarbeiterliste. Wer erwähnt wird, liest der
              Server beim Speichern selbst aus dem Text (SPEC B2). */}
          <ErwaehnungTextarea
            value={entwurf}
            onChange={setEntwurf}
            rows={4}
            autoFocus
            placeholder="Notiz — mit @ jemanden erwähnen"
            className="min-h-[44px] w-full rounded-md border border-line bg-cream px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => void speichern()} disabled={laeuft}>
              {laeuft ? 'Speichern …' : 'Speichern'}
            </Button>
            <Button
              variant="ghost"
              disabled={laeuft}
              onClick={() => {
                setEntwurf(value ?? '');
                setEditing(false);
              }}
            >
              Abbrechen
            </Button>
          </div>
        </div>
      ) : value ? (
        <button
          type="button"
          onClick={() => {
            setEntwurf(value);
            setEditing(true);
          }}
          aria-label="Notiz zur Bestellung bearbeiten"
          className="group block min-h-[44px] w-full rounded-md border-2 border-gold border-l-[6px] bg-cream-warm px-4 py-3 text-left"
        >
          <span className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gold-dark">
            <StickyNote aria-hidden className="h-4 w-4" />
            Notiz
            <Pencil aria-hidden className="ml-auto h-3.5 w-3.5 text-ink-faint group-hover:text-akzent" />
          </span>
          {/* Umbrüche bleiben stehen: Notizen sind oft kleine Listen. */}
          <span className="block whitespace-pre-wrap break-words font-serif text-base text-ink">{value}</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => {
            setEntwurf('');
            setEditing(true);
          }}
          className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-ink-soft hover:text-akzent"
        >
          <Pencil className="h-4 w-4" />
          Notiz hinzufügen
        </button>
      )}
    </section>
  );
}
